import {
  HOUR,
  MINUTE,
  type AvailableCloser,
  type SmallCloser,
  type CalendarClosersInput,
  type CalendarClosersSuccess,
  type UserClosersInput,
  type UserClosersSuccess,
} from '@terros/common'
import { useUserCache } from '../user'
import { useTantalimQuery } from '../query'
import { TARGET_MISSING } from '../permissions'
import { calendarClosersKey } from '../keys'
import { type GetHookResponse } from '../hooks'

export function useClosers(input: CalendarClosersInput): GetHookResponse<AvailableCloser[]> {
  return useTantalimQuery<CalendarClosersInput, CalendarClosersSuccess, AvailableCloser[]>({
    url: '/calendar/closers',
    queryKey: calendarClosersKey(input),
    input,
    convertResponse: (data) => sortClosers(data.closers),
    options: {
      refetchOnMount: 'always',
      gcTime: HOUR,
      staleTime: 15 * MINUTE,
    },
  })
}

function sortClosers(closers: AvailableCloser[]): AvailableCloser[] {
  const assignable: AvailableCloser[] = []
  const locked: AvailableCloser[] = []
  const unavailable: AvailableCloser[] = []
  const outOfServiceArea: AvailableCloser[] = []
  closers.forEach((closer) => {
    if (!closer.inServiceArea) {
      outOfServiceArea.push(closer)
      return
    }
    if (!closer.available) {
      unavailable.push(closer)
      return
    }
    if (closer.locked) {
      locked.push(closer)
      return
    }
    assignable.push(closer)
  })
  return [...assignable, ...locked, ...unavailable, ...outOfServiceArea]
}

export function useEntityClosers({
  entityId = TARGET_MISSING,
  ...props
}: Partial<UserClosersInput>): GetHookResponse<SmallCloser[]> {
  const cache = useUserCache()
  const input = { entityId, ...props }
  return useTantalimQuery<UserClosersInput, UserClosersSuccess, SmallCloser[]>({
    url: '/user/closers',
    queryKey: ['user/closers', input],
    input,
    convertResponse: ({ closers }) => {
      closers.forEach((closer) => cache.setTinyUser(closer))
      return closers
    },
    options: {
      gcTime: HOUR,
      staleTime: 15 * MINUTE,
    },
  })
}
